// pages/Contact.js
import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import { Mail, Instagram, Linkedin, Twitter } from 'lucide-react';

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const formRef = useRef();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await emailjs.sendForm(
        'YOUR_SERVICE_ID', // Replace with your EmailJS service ID
        'YOUR_TEMPLATE_ID', // Replace with your EmailJS template ID
        formRef.current,
        'YOUR_PUBLIC_KEY' // Replace with your EmailJS public key
      );
      setSubmitStatus('success');
      formRef.current.reset();
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const socialLinks = [
    {
      name: 'Instagram',
      icon: Instagram,
      url: 'https://instagram.com',
      color: 'hover:text-pink-500'
    },
    {
      name: 'LinkedIn',
      icon: Linkedin,
      url: 'https://linkedin.com',
      color: 'hover:text-blue-500'
    },
    {
      name: 'Twitter',
      icon: Twitter,
      url: 'https://twitter.com',
      color: 'hover:text-blue-400'
    }
  ];

  return (
    <div className="min-h-screen pt-8 px-4 lg:pt-0 lg:px-0">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-12">
        <h1 className="text-4xl font-bold mb-4 text-center dark:text-white">Get in Touch</h1>
        <p className="text-center text-lg text-neutral-600 dark:text-neutral-400">
          Feel free to reach out for collaborations, inquiries, or just to say hello!
        </p>
      </div>

      <div className="max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Contact Information */}
          <div className="space-y-8">
            <div className="bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm rounded-lg p-6 shadow-lg">
              <h2 className="text-xl font-semibold mb-4 dark:text-white">Contact Information</h2>
              <div className="space-y-4">
                <div className="flex items-center text-neutral-600 dark:text-neutral-400">
                  <Mail className="w-5 h-5 mr-3" />
                  <a href="mailto:elcebstudios@gmail.com" 
                    className="hover:text-neutral-900 dark:hover:text-white transition-colors">
                    elcebstudios@gmail.com
                  </a>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm rounded-lg p-6 shadow-lg">
              <h2 className="text-xl font-semibold mb-4 dark:text-white">Connect With Me</h2>
              <div className="flex flex-col space-y-4">
                {socialLinks.map((social) => {
                  const Icon = social.icon;
                  return (
                    <a
                      key={social.name}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center text-neutral-600 dark:text-neutral-400 ${social.color} transition-colors`}
                    >
                      <Icon className="w-5 h-5 mr-3" />
                      {social.name}
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm rounded-lg p-6 shadow-lg">
            <h2 className="text-xl font-semibold mb-6 dark:text-white">Send a Message</h2>
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" 
                  className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full px-4 py-2 rounded-md border border-neutral-300 dark:border-neutral-600 
                    bg-white/50 dark:bg-neutral-700/50 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 
                    outline-none transition-colors dark:text-white"
                />
              </div>

              <div>
                <label htmlFor="email" 
                  className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-2 rounded-md border border-neutral-300 dark:border-neutral-600 
                    bg-white/50 dark:bg-neutral-700/50 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 
                    outline-none transition-colors dark:text-white"
                />
              </div>

              <div>
                <label htmlFor="subject" 
                  className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2 rounded-md border border-neutral-300 dark:border-neutral-600 
                    bg-white/50 dark:bg-neutral-700/50 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 
                    outline-none transition-colors dark:text-white"
                />
              </div>

              <div>
                <label htmlFor="message" 
                  className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows="4"
                  required
                  className="w-full px-4 py-2 rounded-md border border-neutral-300 dark:border-neutral-600 
                    bg-white/50 dark:bg-neutral-700/50 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 
                    outline-none transition-colors dark:text-white resize-none"
                ></textarea>
              </div>

              {submitStatus === 'success' && (
                <div className="text-green-600 dark:text-green-400 text-sm">
                  Message sent successfully!
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="text-red-600 dark:text-red-400 text-sm">
                  Failed to send message. Please try again later.
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full px-6 py-3 rounded-md bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 
                  font-medium transition-colors ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 
                  'hover:bg-neutral-800 dark:hover:bg-neutral-100'}`}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;